import { useState } from "react";
import { Eyebrow, serifEm } from "./SectionHeading";

const faqs = [
  {
    question: "What is Fluvo?",
    answer:
      "Fluvo is an AI-powered marketing automation platform that connects directly to your database, generates user segments, SQL queries, and personalized campaigns in minutes. Unlike traditional tools, Fluvo learns and adapts over time, so your comms keep improving automatically.",
  },

  {
    question: "How does Fluvo work?",
    answer:
      "Simply connect your database (read-only), tell Fluvo a bit about your business, and the AI will auto-generate segments, queries, and comms flows. From there, Fluvo monitors performance and rewrites campaigns to improve results over time.",
  },
  {
    question: "How is Fluvo different from Customer.io or Vero?",
    answer:
      "While Customer.io and Vero are powerful, they require manual setup, SQL knowledge, and constant tweaking. Fluvo automates all of that: segments, copy, and campaign logic. You can launch and improve campaigns without a marketing team or dev support.",
  },
  {
    question: "Is my data safe with Fluvo?",
    answer:
      "Yes. Fluvo only requires read-only access to your database. Your data never leaves your environment except for securely generating segments and campaigns, and we follow strict security best practices.",
  },
  {
    question: "Who is Fluvo for?",
    answer:
      "Fluvo is designed for startups, growth/product/marketing teams, and agencies that want to launch lifecycle comms quickly without the overhead of coding, SQL, or manual copywriting. It’s perfect for teams without a full-time marketing function or those who find tools like Customer.io or Vero too complex.",
  },
  {
    question: "How quickly can I launch my first campaign?",
    answer:
      "In minutes. Once you connect your database and provide your website URL, Fluvo automatically generates your first user journeys and campaigns. No coding, tagging, or setup required.",
  },
  {
    question: "Can I customize the messages Fluvo creates?",
    answer:
      "Absolutely. Fluvo generates personalized copy in your brand voice, but you can edit, tweak, or completely override any message before or after it goes live.",
  },
  {
    question: "What channels does Fluvo support?",
    answer:
      "Fluvo currently supports email, SMS, and push notifications. More channels will be added as we grow.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      className="bg-[#fafafa] px-6 py-28 lg:px-10 lg:py-36"
      style={{ fontFamily: "var(--font-geist)" }}
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-6 text-[clamp(32px,3.8vw,52px)] font-medium leading-[1] tracking-[-0.03em] text-neutral-900">
            Frequently asked <span style={serifEm}>questions</span>
          </h2>
          <p className="mt-6 text-[15px] text-neutral-500">
            Still curious?{" "}
            <a
              href="mailto:hello@fluvo.ai"
              className="underline underline-offset-4"
            >
              hello@fluvo.ai
            </a>
          </p>
        </div>

        <dl className="border-t border-neutral-200 lg:col-span-8">
          {faqs.map((faq, i) => {
            const isOpen = open === i;
            return (
              <div key={faq.question} className="border-b border-neutral-200">
                <dt>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-6 py-5 text-left"
                  >
                    <span className="text-[16px] font-medium text-neutral-900">
                      {faq.question}
                    </span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-[16px] leading-none text-neutral-500">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                </dt>
                {isOpen && (
                  <dd className="pb-6 pr-12 text-[15px] leading-[1.6] text-neutral-500">
                    {faq.answer}
                  </dd>
                )}
              </div>
            );
          })}
        </dl>
      </div>
    </section>
  );
}
